import classNames from "classnames";
import { SearchInput } from "components";
import { type RemoveContentObserverInput } from "components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import {
  ContentTypesEnum,
  ItemsTypes,
  RolePermissions,
  RouteNames,
  SortingDirection,
  type SortOptions,
  TemplateTypes,
  ViewType,
} from "enums";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useHasPermissions } from "features/Application/hooks";
import {
  ScormPublishSuccess,
  LockScormSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { useObservable } from "hooks/useObservable";
import { useRtn } from "hooks/useRtn";
import { bindAction } from "interfaces";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "interfaces/onSelectionChanged";
import { isEmpty, noop } from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { useNavigate } from "react-router";
import { type Filters } from "utils/queryUtils";
import GenericItemsView from "views/ItemsView/GenericItemsView";
import {
  resetAppliedFilter,
  resetPagination,
  resetSearch,
  resetSortingColumnName,
  resetSortingDirection,
  setAppliedFilter,
  setPlainSortingDirection,
  setSearch,
  setSortingColumnName,
  setSortingDirection,
} from "../state/slices/FiltersSlice";
import { reset, setSelected } from "../state/slices/OverviewSlice";
import { lockItemAction, publishItemAction } from "../state/thunks/EntityStateThunk";
import { getOverviewFilterOptions } from "../state/thunks/FiltersThunk";
import { fetchAction, deleteAction, changeVisibilityAction, updateScormAction } from "../state/thunks/OverviewThunk";
import { type ScormOverview } from "../types/models";
import { Columns, columnToParamMap, getColumnOptions, sortOptions } from "./components/columnOptions";
import { NoResults } from "./components/NoResults";
import { ScormOverviewHeader } from "./components/OverviewHeader/OverviewHeader";
import { FilterForm } from "./components/FilterForm";
import DeleteContentConfirmationModal from "../../../../components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import { AssetVisibilityConfirmationModal } from "../../../../components/assetVisibilityConfirmationModal/AssetVisibilityConfirmationModal";
import SortDirections from "../../../../enums/sortDirections";
import type { CardsViewerItem } from "../../../../components/cardsViewer/types";
import { OverviewCard } from "./components/OverviewCard";
import PublishConfirmationModal, {
  type EntityState,
} from "../../../../components/modal/PublishConfirmationModal/PublishConfirmationModal";
import { NotifyStepConfig } from "../../../SystemNotifications/types";
import { SwitchEntityTypes } from "../../../../enums/switchEntityTypes";

export type OverviewProps = PropsFromRedux;

export const Overview = (props: OverviewProps) => {
  const {
    items,
    itemsCount,
    isLoading,
    areAllLoaded,
    selected,
    filters,
    fetchData,
    deleteItem,
    changeVisibility,
    lockItem,
    setSelected,
    getFilterOptions,
    applyFilter,
    resetAppliedFilter,
    resetSearchFilters,
    resetData,
    onSearchChanged,
    setListSortingColumnName,
    setListSortingDirection,
    setGridSortingDirection,
    publishItem,
    updateScorm,
  } = props;
  const navigate = useNavigate();
  const hasCreatePermission = useHasPermissions([RolePermissions.AssetsCreate]);

  const onTriggerRemovalObserver =
    useObservable<(onConfirm: () => void, removeContentInput: RemoveContentObserverInput) => void>();
  const onTriggerChangeVisibilityObserver =
    useObservable<(onConfirm: () => void, hidden: boolean, ids: number[]) => void>();
  const onPublishObserver = useObservable<(onConfirm: () => void, entityState: EntityState) => void>();

  const reloadListItemsRef = useRef<(enableSorting: boolean) => void>();
  const [purchasedSelectedCount, setPurchasedSelectedCount] = useState(0);
  const [hiddenSelectedCount, setHiddenSelectedCount] = useState(0);
  const [visibleSelectedCount, setVisibleSelectedCount] = useState(0);
  const [draftSelectedCount, setDraftSelectedCount] = useState(0);
  const [selectedViewType, setSelectedViewType] = useState(ViewType.GRID);
  const isGridView = useMemo(() => selectedViewType === ViewType.GRID, [selectedViewType]);

  useEffect(() => {
    return () => {
      resetData();
      if (!window.location.pathname.includes(`/${RouteNames.scorm}`)) resetSearchFilters();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToEditEntity = (payload: { id: number }) => navigate(`${payload.id}/configuration`);
  const updateScormHandler = useCallback((payload: { id: number }) => updateScorm(payload.id), [updateScorm]);
  useRtn(LockScormSuccess, goToEditEntity);
  useRtn(ScormPublishSuccess, updateScormHandler);

  const onSelectionChanged = (args: SelectionChangedArgs<ScormOverview>) => {
    let purchasedCount = 0;
    let draftCount = 0;
    let hiddenCount = 0;
    let visibleCount = 0;

    const onAdded = (item: ScormOverview) => {
      item.isPurchased && ++purchasedCount;
      item.isDraft && ++draftCount;
      !item.visibility && ++hiddenCount;
      item.visibility && ++visibleCount;
    };
    const onRemoved = (item: ScormOverview) => {
      item.isPurchased && --purchasedCount;
      item.isDraft && --draftCount;
      !item.visibility && --hiddenCount;
      item.visibility && --visibleCount;
    };

    HandleOnSelectionChanged(args, onAdded, onRemoved, clearSelection);

    setPurchasedSelectedCount(purchasedSelectedCount + purchasedCount);
    setDraftSelectedCount(draftSelectedCount + draftCount);
    setHiddenSelectedCount(hiddenSelectedCount + hiddenCount);
    setVisibleSelectedCount(visibleSelectedCount + visibleCount);
  };

  const onViewTypeChange = (viewType: ViewType) => {
    resetData();
    clearSelection();
    setSelectedViewType(viewType);
    if (viewType === ViewType.GRID) {
      loadCards(0);
    }
  };

  const clearSelection = useCallback(() => {
    setSelected([]);
    setPurchasedSelectedCount(0);
    setHiddenSelectedCount(0);
    setVisibleSelectedCount(0);
    setDraftSelectedCount(0);
  }, [setSelected]);

  const handleSearchChanged = (search: string) => {
    onSearchChanged(search);
    refetchItemsFromStart();
  };

  const handleFetch = (skip?: number, top?: number, sortingColumnName?: string, sortDirection?: SortingDirection) => {
    isGridView
      ? loadCards(items.length, sortingColumnName, sortDirection)
      : loadList(skip, top, sortingColumnName, sortDirection);
  };

  const loadList = useCallback(
    (
      skip: number = 0,
      top: number = 10,
      sortingColumnName: string = Columns.Added,
      sortingDirection: SortingDirection = SortingDirection.Descending,
    ) => {
      const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<Columns>];
      fetchData(skip, top, sortBy, sortingDirection);
    },
    [fetchData],
  );

  const loadCards = useCallback(
    (skip: number, sortBy: string = "dateCreated", sortDirection: string = SortDirections.Desc) => {
      fetchData(skip, Number(process.env.REACT_APP_LOAD_ITEMS_COUNT), sortBy, sortDirection, true);
    },
    [fetchData],
  );

  const refetchItemsFromStart = useCallback(() => {
    if (isGridView) {
      resetData();
      loadCards(0, filters.sortingColumnName, filters.sortingDirection);
    } else {
      // @ts-ignore
      reloadListItemsRef.current?.(isEmpty(filters.search));
    }
  }, [filters.search, filters.sortingColumnName, filters.sortingDirection, isGridView, loadCards, resetData]);

  const handleEdit = useCallback(
    (id: number, isDraft: boolean) => {
      isDraft ? navigate(`${id}/configuration`) : lockItem(id);
    },
    [navigate, lockItem],
  );

  const handleDelete = useCallback(
    (ids: number[], flowsCount?: number, packsCount?: number) => {
      onTriggerRemovalObserver.notify(
        () => {
          deleteItem(ids, refetchItemsFromStart);
          clearSelection();
        },
        {
          selectedItemsCount: ids.length,
          flowsCount,
          packsCount,
        },
      );
    },
    [onTriggerRemovalObserver, deleteItem, refetchItemsFromStart, clearSelection],
  );

  const handlePublish = useCallback(
    (id: number, flowsCount: number, hasBeenPublished: boolean) => {
      const entityState: EntityState = {
        itemsCount: flowsCount,
        entityId: id,
        templateType: TemplateTypes.AssessmentChanged,
        notifyConfigType: NotifyStepConfig.WithProgress,
        entityType: SwitchEntityTypes.Scorm,
        hasBeenPublished,
      };

      const cb = hasBeenPublished
        ? () => onPublishObserver.notify(noop, entityState)
        : () => publishItem(entityState.entityId);
      cb();
    },
    [publishItem, onPublishObserver],
  );

  const handleToggleVisibility = useCallback(
    (ids: number[], hidden: boolean) => () => {
      onTriggerChangeVisibilityObserver.notify(
        () => {
          changeVisibility(ids, hidden, refetchItemsFromStart);
          clearSelection();
        },
        hidden,
        ids,
      );
    },
    [onTriggerChangeVisibilityObserver, changeVisibility, refetchItemsFromStart, clearSelection],
  );

  const columnOptions = useMemo(() => {
    return getColumnOptions({
      readonly: !hasCreatePermission,
      hideDisabled: !hasCreatePermission,
      handleEdit,
      handleDelete,
      handlePublish,
      handleToggleVisibility,
    });
  }, [handleDelete, handleToggleVisibility, handleEdit, handlePublish, hasCreatePermission]);

  const onGridSortChange = async (_: React.SyntheticEvent<HTMLElement>, data: { value?: SortOptions }) => {
    resetData();
    clearSelection();

    const [sortBy, sortDirection] = data.value!.split(" ");
    setSortingColumnName(sortBy);
    setGridSortingDirection(sortDirection);
    loadCards(0, sortBy, sortDirection);
  };

  const applyNewFilter = useCallback(
    (newFilter: Filters) => {
      clearSelection();
      applyFilter(newFilter);
      if (isGridView) {
        resetData();
        loadCards(0, filters.sortingColumnName, filters.sortingDirection);
      }
    },
    [
      applyFilter,
      clearSelection,
      filters.sortingColumnName,
      filters.sortingDirection,
      isGridView,
      loadCards,
      resetData,
    ],
  );

  const resetFilter = useCallback(() => {
    clearSelection();
    resetAppliedFilter();
    if (isGridView) {
      resetData();
      loadCards(0);
    }
  }, [clearSelection, resetAppliedFilter, isGridView, loadCards, resetData]);

  return (
    <section className={classNames("nested-content")}>
      <ScormOverviewHeader
        purchasedSelected={purchasedSelectedCount > 0}
        hiddenSelected={hiddenSelectedCount > 0}
        visibleSelected={visibleSelectedCount > 0}
        draftSelected={draftSelectedCount > 0}
        selectedIds={selected}
        clearSelection={clearSelection}
        handleDelete={handleDelete}
        handleToggleVisibility={handleToggleVisibility}
      />
      <GenericItemsView
        blur
        items={items}
        itemsType={ItemsTypes.Scorm}
        viewType={selectedViewType}
        isLoading={isLoading}
        columnOptions={columnOptions}
        fetchData={handleFetch}
        dataCount={itemsCount}
        renderSearch={() => (
          <SearchInput
            placeholder="Search for SCORM Packages..."
            onChange={handleSearchChanged}
            defaultValue={filters.search}
          />
        )}
        selectedIds={selected}
        isSelectDisabled={() => !hasCreatePermission}
        onSelectionChanged={onSelectionChanged}
        onSelectedGridItemsChanged={setSelected}
        onSelectedItemChanged={setSelected}
        // @ts-ignore
        getFilterForm={() => <FilterForm />}
        getFilterOptions={getFilterOptions}
        filterOptions={filters.filterOptions}
        filterOptionsLoading={filters.isLoading}
        applyFilter={applyNewFilter}
        resetFilter={resetFilter}
        setReloadListItems={(reloadListItems) => (reloadListItemsRef.current = reloadListItems)}
        appliedFilter={filters.appliedFilter}
        sortingDirection={filters.sortingDirection}
        sortOptions={sortOptions}
        onLoad={isGridView ? () => loadCards(0) : noop}
        onSortChange={onGridSortChange}
        setSortingColumnName={setListSortingColumnName}
        setSortingDirection={setListSortingDirection}
        defaultSortingColumnName={Columns.Added}
        onViewTypeChange={onViewTypeChange}
        isAllDataLoaded={areAllLoaded}
        listViewRtnEvents={listViewRtnEvents}
        renderCard={(props: CardsViewerItem<ScormOverview>) => (
          <OverviewCard
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handlePublish={handlePublish}
            handleToggleVisibility={handleToggleVisibility}
            readonly={!hasCreatePermission}
            hideDisabled={!hasCreatePermission}
          />
        )}
        noResultsContent={<NoResults filtered={!isEmpty(filters.appliedFilter) || !isEmpty(filters.search)} />}
        permissions={listViewPermissions}
      />
      <DeleteContentConfirmationModal
        contentType={ContentTypesEnum.Scorm}
        onTriggerRemoveContentObserver={onTriggerRemovalObserver}
      />
      <PublishConfirmationModal
        onTriggerPublishContentObserver={onPublishObserver}
        onConfirmAction={publishItem}
        contentType="SCORM Package"
        dependencyType="flow"
      />
      <AssetVisibilityConfirmationModal observer={onTriggerChangeVisibilityObserver} assetType="SCORM" />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { overview, filters } = state.library.scorm;
  return { ...overview, filters };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    resetData: bindAction(reset, dispatch),
    fetchData: bindAction(fetchAction, dispatch),
    deleteItem: bindAction(deleteAction, dispatch),
    changeVisibility: bindAction(changeVisibilityAction, dispatch),
    publishItem: bindAction(publishItemAction, dispatch),
    updateScorm: bindAction(updateScormAction, dispatch),
    lockItem: bindAction(lockItemAction, dispatch),
    getFilterOptions: bindAction(getOverviewFilterOptions, dispatch),
    onSearchChanged: bindAction(setSearch, dispatch),
    setSelected: bindAction(setSelected, dispatch),
    applyFilter: bindAction(setAppliedFilter, dispatch),
    resetAppliedFilter: bindAction(resetAppliedFilter, dispatch),
    setListSortingColumnName: bindAction(setSortingColumnName, dispatch),
    setListSortingDirection: bindAction(setSortingDirection, dispatch),
    setGridSortingDirection: bindAction(setPlainSortingDirection, dispatch),
    resetSearchFilters: () => {
      dispatch(resetSearch());
      dispatch(resetPagination());
      dispatch(resetAppliedFilter());
      dispatch(resetSortingColumnName());
      dispatch(resetSortingDirection());
    },
  };
};

const listViewPermissions = [RolePermissions.AssetsManage];
const listViewRtnEvents = [ScormPublishSuccess];

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(Overview);

export default ConnectedComponent;
